// TDS2 SHIM — composes SystemErrorState; no direct TDS2 counterpart.
// Specialized error state for AgentScript compilation failures coming back
// from the deploy controller. Accepts either an array of structured error
// records (`{ line, message, type }`) or pre-formatted strings.
import React from 'react';
import PropTypes from 'prop-types';
import SystemErrorState from './SystemErrorState';

function CircleAlertIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function formatError(err) {
  if (typeof err === 'string') return err;
  if (!err) return '';
  const message = err.message || err.description || '';
  if (err.line != null) return 'Line ' + err.line + ': ' + message;
  return message;
}

export default function CompilationErrorState({
  errors = [],
  eyebrow = 'Deploy · Compilation failed',
  title = 'Compilation failed',
  body = 'The AgentScript didn’t compile. Fix the errors below and try again.',
  retryLabel = 'Try again',
  onRetry,
}) {
  const lines = (errors || []).map(formatError).filter((s) => s.length > 0);

  const errorList = lines.length > 0 ? (
    <pre
      className="tds2-agentforce-state-card__errors"
      aria-label="Compilation errors"
    >
      {lines.join('\n')}
    </pre>
  ) : null;

  const bodyNode = (
    <React.Fragment>
      {body ? <p>{body}</p> : null}
      {errorList}
    </React.Fragment>
  );

  const actions = onRetry ? (
    <button
      type="button"
      className="tds2-button tds2-button--primary tds2-button--small"
      data-tds2-shim="Button"
      onClick={onRetry}
    >
      <span className="tds2-button__label">{retryLabel}</span>
    </button>
  ) : null;

  return (
    <SystemErrorState
      variant="danger"
      icon={<CircleAlertIcon />}
      eyebrow={eyebrow}
      title={title}
      body={bodyNode}
      actions={actions}
    />
  );
}

CompilationErrorState.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        line: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        message: PropTypes.string,
        description: PropTypes.string,
        type: PropTypes.string,
      }),
    ])
  ),
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  retryLabel: PropTypes.string,
  onRetry: PropTypes.func,
};

export { formatError };
